import { useEffect, useState } from 'react';

/**
 * Formats a number to always have at least two digits.
 * @param value - The number to format.
 * @returns A string representation with at least two digits.
 */
const formatDigits = (value: number): string => {
    return value < 10 ? `0${value}` : `${value}`;
};

/**
 * Custom hook to create a countdown timer.
 * @param targetDate - The target date string in a format parseable by Date.
 * @returns An object containing days, hours, minutes, and seconds remaining.
 */
const useCountdownTimer = (
    targetDate: string | undefined | null,
): {
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
} => {
    const [days, setDays] = useState<string>('00');
    const [hours, setHours] = useState<string>('00');
    const [minutes, setMinutes] = useState<string>('00');
    const [seconds, setSeconds] = useState<string>('00');

    useEffect(() => {
        if (!targetDate) {
            // If no target date is provided, reset all values to '00'
            setDays('00');
            setHours('00');
            setMinutes('00');
            setSeconds('00');
            return;
        }

        const formattedDate = targetDate.replace(' ', 'T');
        const countdownDate = new Date(formattedDate).getTime();

        // Validate the countdownDate
        if (isNaN(countdownDate)) {
            console.error(`Invalid targetDate provided to useCountdownTimer: ${targetDate}`);
            setDays('00');
            setHours('00');
            setMinutes('00');
            setSeconds('00');
            return;
        }

        const interval = setInterval(() => {
            const now = new Date().getTime();
            const distance = countdownDate - now;

            if (distance > 0) {
                const dayCount = Math.floor(distance / (1000 * 60 * 60 * 24));
                const hourCount = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minuteCount = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                const secondCount = Math.floor((distance % (1000 * 60)) / 1000);

                setDays(formatDigits(dayCount));
                setHours(formatDigits(hourCount));
                setMinutes(formatDigits(minuteCount));
                setSeconds(formatDigits(secondCount));
            } else {
                clearInterval(interval);
                setDays('00');
                setHours('00');
                setMinutes('00');
                setSeconds('00');
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [targetDate]);

    return { days, hours, minutes, seconds };
};

export default useCountdownTimer;
